import { useState, useCallback } from "react";
import { MapPin, ArrowRightLeft, X, Loader2, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";

const PUBLIC_API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : "/api";

interface CityRecord {
  id: number;
  name: string;
  bolge: number;
}

interface RegionsResponse {
  data: CityRecord[];
}

interface YerelKalkinmaKonu {
  id: number;
  description: string;
  sectors: Array<{ id: number; name: string }>;
}

interface YerelKalkinmaResponse {
  success: boolean;
  data: YerelKalkinmaKonu[];
  error?: { message: string };
}

const regionLabels: Record<number, string> = {
  1: "1. Bölge",
  2: "2. Bölge",
  3: "3. Bölge",
  4: "4. Bölge",
  5: "5. Bölge",
  6: "6. Bölge",
};

const regionColors: Record<number, string> = {
  1: "border-blue-500/30 bg-blue-500/5",
  2: "border-emerald-500/30 bg-emerald-500/5",
  3: "border-amber-500/30 bg-amber-500/5",
  4: "border-orange-500/30 bg-orange-500/5",
  5: "border-rose-500/30 bg-rose-500/5",
  6: "border-purple-500/30 bg-purple-500/5",
};

const regionHeaderColors: Record<number, string> = {
  1: "text-blue-400",
  2: "text-emerald-400",
  3: "text-amber-400",
  4: "text-orange-400",
  5: "text-rose-400",
  6: "text-purple-400",
};

const regionBadgeColors: Record<number, string> = {
  1: "bg-blue-500/20 text-blue-300",
  2: "bg-emerald-500/20 text-emerald-300",
  3: "bg-amber-500/20 text-amber-300",
  4: "bg-orange-500/20 text-orange-300",
  5: "bg-rose-500/20 text-rose-300",
  6: "bg-purple-500/20 text-purple-300",
};

export function DataRegionsPage() {
  const { data, loading, error, refetch } =
    useFetch<RegionsResponse>("/data/regions");

  const [changeBolgeCity, setChangeBolgeCity] = useState<CityRecord | null>(null);
  const [newBolge, setNewBolge] = useState<number>(1);
  const [changeSaving, setChangeSaving] = useState(false);
  const [changeBolgeError, setChangeBolgeError] = useState<string | null>(null);

  const [expandedCity, setExpandedCity] = useState<CityRecord | null>(null);
  const [ykData, setYkData] = useState<YerelKalkinmaKonu[] | null>(null);
  const [ykLoading, setYkLoading] = useState(false);
  const [ykError, setYkError] = useState<string | null>(null);

  const fetchYerelKalkinma = useCallback(async (city: CityRecord) => {
    if (expandedCity?.id === city.id) {
      setExpandedCity(null);
      setYkData(null);
      setYkError(null);
      return;
    }

    setExpandedCity(city);
    setYkLoading(true);
    setYkError(null);
    setYkData(null);

    try {
      const res = await fetch(`${PUBLIC_API_BASE}/yerel-kalkinma/il/${city.id}`);
      const json: YerelKalkinmaResponse = await res.json();

      if (!res.ok || !json.success) {
        if (res.status === 404) {
          setYkData([]);
        } else {
          setYkError(json.error?.message || "Veriler alınamadı.");
        }
      } else {
        setYkData(json.data);
      }
    } catch {
      setYkError("Yerel kalkınma verileri alınırken bir hata oluştu.");
    } finally {
      setYkLoading(false);
    }
  }, [expandedCity]);

  function openChangeBolge(city: CityRecord) {
    setChangeBolgeCity(city);
    setNewBolge(city.bolge);
    setChangeBolgeError(null);
  }

  function closeChangeBolge() {
    setChangeBolgeCity(null);
    setChangeSaving(false);
  }

  async function handleChangeBolge() {
    if (!changeBolgeCity) return;
    if (newBolge === changeBolgeCity.bolge) {
      closeChangeBolge();
      return;
    }

    setChangeSaving(true);
    try {
      // TODO: PUT /data/regions/:ilId endpoint does not exist yet.
      // When the API is ready, uncomment the following:
      // await adminFetch(`/data/regions/${changeBolgeCity.id}`, {
      //   method: "PUT",
      //   body: JSON.stringify({ bolge: newBolge }),
      // });
      // closeChangeBolge();
      // refetch();

      // For now, just show that the API is not ready
      setChangeBolgeError("Bölge değiştirme API'si henüz hazır değil.");
      setChangeSaving(false);
    } catch {
      // handled
      closeChangeBolge();
    }
  }

  if (loading) return <PageLoading />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data) return null;

  // API returns flat array of { id, name, bolge } -- group by bolge
  const grouped = data.data.reduce(
    (acc, city) => {
      const region = city.bolge;
      if (!acc[region]) acc[region] = [];
      acc[region].push(city);
      return acc;
    },
    {} as Record<number, CityRecord[]>
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Yatırım teşvik bölgeleri ve şehir dağılımları. Şehirlere tıklayarak yerel kalkınma konularını görüntüleyebilirsiniz.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(grouped)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([region, cities]) => {
            const regionNum = Number(region);
            return (
              <div
                key={region}
                className={cn(
                  "rounded-xl border p-5",
                  regionColors[regionNum] || "border-border bg-card"
                )}
              >
                <div className="flex items-center gap-2">
                  <MapPin
                    className={cn(
                      "h-4 w-4",
                      regionHeaderColors[regionNum] || "text-muted-foreground"
                    )}
                  />
                  <h3
                    className={cn(
                      "text-sm font-semibold",
                      regionHeaderColors[regionNum] || "text-foreground"
                    )}
                  >
                    {regionLabels[regionNum] || `${region}. Bölge`}
                  </h3>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {cities.length} şehir
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {cities.sort((a, b) => a.name.localeCompare(b.name, "tr")).map((city) => (
                    <div key={city.id} className="group relative">
                      <button
                        onClick={() => fetchYerelKalkinma(city)}
                        className={cn(
                          "flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors",
                          expandedCity?.id === city.id
                            ? "bg-accent text-accent-foreground"
                            : "bg-secondary text-foreground hover:bg-secondary/80"
                        )}
                      >
                        {city.name}
                        {expandedCity?.id === city.id ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-0 group-hover:opacity-100" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openChangeBolge(city);
                        }}
                        className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-secondary text-muted-foreground shadow-sm hover:bg-accent hover:text-accent-foreground group-hover:flex"
                        aria-label="Bölge değiştir"
                        title="Bölge Değiştir"
                      >
                        <ArrowRightLeft className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
      </div>

      {/* Yerel Kalkınma Panel */}
      {expandedCity && (
        <div className="rounded-xl border border-border/60 bg-card p-5 ring-1 ring-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-accent" />
              <h3 className="text-sm font-semibold text-foreground">
                {expandedCity.name} - Yerel Kalkınma Konuları
              </h3>
              <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", regionBadgeColors[expandedCity.bolge] || "bg-secondary text-foreground")}>
                {regionLabels[expandedCity.bolge]}
              </span>
            </div>
            <button
              onClick={() => {
                setExpandedCity(null);
                setYkData(null);
                setYkError(null);
              }}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Kapat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4">
            {ykLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Yükleniyor...</span>
              </div>
            ) : ykError ? (
              <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {ykError}
              </div>
            ) : ykData && ykData.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                Bu il için yerel kalkınma konusu bulunamadı.
              </div>
            ) : ykData ? (
              <div className="space-y-2">
                {ykData.map((konu) => (
                  <div
                    key={konu.id}
                    className="rounded-lg border border-border bg-secondary/50 px-3 py-2"
                  >
                    <p className="text-sm text-foreground">{konu.description}</p>
                    {konu.sectors.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {konu.sectors.map((sektor) => (
                          <span
                            key={sektor.id}
                            className="rounded-md bg-accent/10 px-1.5 py-0.5 text-xs text-accent"
                          >
                            {sektor.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Bölge Değiştir Dialog */}
      {changeBolgeCity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/60"
            onClick={closeChangeBolge}
          />
          <div className="relative z-10 w-full max-w-sm rounded-xl border border-border/60 bg-card p-6 shadow-xl ring-1 ring-white/5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">
                Bölge Değiştir
              </h2>
              <button
                onClick={closeChangeBolge}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Kapat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <p className="text-sm text-foreground">
                  <span className="font-medium">{changeBolgeCity.name}</span> şu anda{" "}
                  <span className={cn("font-medium", regionHeaderColors[changeBolgeCity.bolge])}>
                    {regionLabels[changeBolgeCity.bolge]}
                  </span>
                  'de yer almaktadır.
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Yeni Bölge
                </label>
                <select
                  value={newBolge}
                  onChange={(e) => setNewBolge(Number(e.target.value))}
                  className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  {[1, 2, 3, 4, 5, 6].map((b) => (
                    <option key={b} value={b}>
                      {regionLabels[b]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 px-3 py-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <p className="text-xs text-amber-300">
                  Bu işlem teşvik hesaplamalarını etkileyecektir. Onaylıyor musunuz?
                </p>
              </div>

              {changeBolgeError && (
                <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {changeBolgeError}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeChangeBolge}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  İptal
                </button>
                <button
                  onClick={handleChangeBolge}
                  disabled={changeSaving || newBolge === changeBolgeCity.bolge}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                    (changeSaving || newBolge === changeBolgeCity.bolge) &&
                      "cursor-not-allowed opacity-50"
                  )}
                >
                  {changeSaving && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  Onayla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
